import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setSelectedTab } from '../../../redux/features/chatUi/chatUiSlice';
import { selectSelectedSession } from '../../../domain/selectors/selected/chat/selectSelectedSession';
import { selectSelectedChatUi } from '../../../domain/selectors/selected/chat/selectSelectedChatUi';
import { selectSelectedSessionId } from '../../../domain/selectors/base/chat/selectSelectedSessionId';
import { selectAgentsById } from '../../../domain/selectors/base/agents/selectAgentsById';
import { resolveSessionAgent } from '../../../domain/resolvers/resolveSessionAgent';
import { resolveChatSessionCapabilities } from '../../../domain/resolvers/resolveChatSessionCapabilities';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import { NexusAgent } from '../../../domain/agents/models/Agent';

export type ChatWindowTab = 'chat' | 'documents' | 'persona' | 'overview';

export interface UseChatWindowResult {
    selectedSessionId: string | null;
    hasSession: boolean;
    selectedTab: ChatWindowTab;
    visibleTabs: ChatWindowTab[];
    resolvedAgent: NexusAgent | null;
    onTabSelect: (tab: ChatWindowTab) => void;
}

export const useChatWindow = (): UseChatWindowResult => {
    const dispatch = useAppDispatch();
    const selectedSessionId = useAppSelector(selectSelectedSessionId);
    const selectedSession = useAppSelector(selectSelectedSession);
    const selectedChatUi = useAppSelector(selectSelectedChatUi);
    const agentsById = useAppSelector(selectAgentsById);

    const selectedTab = (selectedChatUi.selectedTab as ChatWindowTab | undefined) ?? 'chat';

    const resolvedAgent = useMemo(() => {
        if (!selectedSession) {
            return null;
        }

        return resolveSessionAgent(selectedSession, agentsById);
    }, [agentsById, selectedSession]);

    const capabilities = useMemo(() => {
        if (!selectedSession) {
            return null;
        }

        return resolveChatSessionCapabilities(selectedSession, resolvedAgent);
    }, [resolvedAgent, selectedSession]);

    const visibleTabs = useMemo((): ChatWindowTab[] => {
        if (!selectedSession) {
            return ['chat'];
        }

        const tabs: ChatWindowTab[] = ['chat'];
        const isProGPT = selectedSession.providerType === ChatProviderType.ProGPT;
        const isNexus = selectedSession.providerType === ChatProviderType.Nexus;

        if (isProGPT || capabilities?.supportsDocumentUpload) {
            tabs.push('documents');
        }

        if (isProGPT) {
            tabs.push('persona');
        }

        if (isNexus) {
            tabs.push('overview');
        }

        return tabs;
    }, [capabilities?.supportsDocumentUpload, selectedSession]);

    // reset to chat tab if the current tab is not available for the selected session
    useEffect(() => {
        if (!selectedSessionId) {
            return;
        }

        if (!visibleTabs.includes(selectedTab)) {
            dispatch(
                setSelectedTab({
                    sessionId: selectedSessionId,
                    selectedTab: 'chat',
                }),
            );
        }
    }, [dispatch, selectedSessionId, selectedTab, visibleTabs]);

    const onTabSelect = useCallback(
        (tab: ChatWindowTab): void => {
            if (!selectedSessionId) {
                return;
            }

            dispatch(
                setSelectedTab({
                    sessionId: selectedSessionId,
                    selectedTab: tab,
                }),
            );
        },
        [dispatch, selectedSessionId],
    );

    return useMemo(
        () => ({
            selectedSessionId,
            hasSession: selectedSession !== null,
            selectedTab,
            visibleTabs,
            resolvedAgent,
            onTabSelect,
        }),
        [onTabSelect, resolvedAgent, selectedSession, selectedSessionId, selectedTab, visibleTabs],
    );
};

export default useChatWindow;
